import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipPortal = TooltipPrimitive.Portal;

const tooltipContentVariants = cva('', {
  variants: {
    padding: {
      default: 'px-[9px] py-[4.5px]',
      noPadding: 'px-0 py-0',
    },
    borderRounding: {
      rounded: 'rounded-md',
      notRounded: 'rounded-none',
    },
  },
  defaultVariants: {
    padding: 'default',
    borderRounding: 'rounded',
  },
});

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    variant?: VariantProps<typeof tooltipContentVariants>;
  }
>(({ className, sideOffset = 4, variant, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      'z-50 overflow-hidden border bg-popover text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      'text-white bg-venn-black bg-opacity-90',
      tooltipContentVariants(variant),
      className,
    )}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

const TooltipContentWithArrow = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    variant?: VariantProps<typeof tooltipContentVariants>;
  }
>(({ asChild, children, ...props }, ref) => (
  <TooltipContent asChild={asChild} ref={ref} {...props}>
    <TooltipPrimitive.Arrow asChild={asChild} />
    {children}
  </TooltipContent>
));
TooltipContentWithArrow.displayName = `${TooltipPrimitive.Content.displayName}WithArrow`;

export {
  Tooltip,
  TooltipTrigger,
  TooltipPortal,
  TooltipContent,
  TooltipContentWithArrow,
  TooltipProvider,
  tooltipContentVariants,
};
